import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile, getModalities, getOfferings } from '../api/client';
import { useAuth } from '../context/AuthContext';
import ModalitySelector, { computeInitialOpenGroups } from './ModalitySelector';
import OfferingFormModal from './OfferingFormModal';

// Derives an initial session_focus from the offerings a coach just entered
// during onboarding, since there's no longer a dedicated "how do you work
// with clients" question here. No offerings yet (skipped straight through)
// preserves the same "show everything" default the old Focus step's
// initial value used — coaches can always narrow it in Settings later.
function deriveSessionFocus(offeringsList) {
  if (offeringsList.length === 0) return 'both';
  const hasGroup = offeringsList.some(o => o.format === 'group');
  const hasIndividual = offeringsList.some(o => o.format !== 'group');
  if (hasGroup && hasIndividual) return 'both';
  return hasGroup ? 'group' : 'individual';
}

export default function OnboardingWizard({ userEmail, onComplete, onStartTour }) {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    practiceName: '',
    modalities: [],
  });
  const [modalityGroups, setModalityGroups] = useState([]);
  const [offerings, setOfferings] = useState([]);

  useEffect(() => {
    getModalities().then(res => setModalityGroups(res.data || [])).catch(() => setModalityGroups([]));
    // Offerings persist immediately when added (shared OfferingFormModal
    // always calls the API directly) — fetch any already created in a
    // prior, abandoned run of this wizard so resuming doesn't show an
    // empty list and risk the coach re-adding duplicates.
    getOfferings().then(res => setOfferings(res.data || [])).catch(() => setOfferings([]));
  }, []);

  const steps = ['welcome', 'about', 'modalities', 'offerings', 'done'];
  const totalSteps = steps.length - 1;
  const stepId = steps[step];

  const handleFinish = async (startTour) => {
    setSaving(true);
    try {
      await updateProfile({
        full_name: form.fullName || undefined,
        practice_name: form.practiceName || undefined,
        modalities: form.modalities,
        session_focus: deriveSessionFocus(offerings),
        onboarding_completed: true,
      });
      await refreshProfile();
    } catch (e) {
      console.error('Onboarding save error', e);
    } finally {
      onComplete();
      if (startTour) onStartTour();
      navigate('/settings');
    }
  };

  const canAdvance = () => {
    if (stepId === 'about') return form.fullName.trim().length > 0;
    return true;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {step > 0 && (
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${(step / totalSteps) * 100}%` }} />
          </div>
        )}

        {stepId === 'welcome' && <StepWelcome onNext={() => setStep(1)} />}
        {stepId === 'about' && (
          <StepAbout form={form} setForm={setForm}
            onBack={() => setStep(0)} onNext={() => setStep(2)} canAdvance={canAdvance()} />
        )}
        {stepId === 'modalities' && (
          <StepModalities form={form} setForm={setForm} modalityGroups={modalityGroups}
            onBack={() => setStep(1)} onNext={() => setStep(3)} />
        )}
        {stepId === 'offerings' && (
          <StepOfferings offerings={offerings} setOfferings={setOfferings}
            modalityGroups={modalityGroups} defaultModalities={form.modalities}
            onBack={() => setStep(2)} onNext={() => setStep(4)} />
        )}
        {stepId === 'done' && (
          <StepDone form={form} saving={saving}
            onTour={() => handleFinish(true)}
            onSkip={() => handleFinish(false)} />
        )}
      </div>
    </div>
  );
}

function StepWelcome({ onNext }) {
  return (
    <div style={styles.stepContent}>
      <div style={styles.logo}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="24" fill="var(--sage)" opacity="0.15" />
          <path d="M24 10c-7.7 0-14 6.3-14 14s6.3 14 14 14 14-6.3 14-14S31.7 10 24 10zm0 4c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4zm0 20c-3.3 0-6.2-1.7-8-4.2.04-2.6 5.4-4.1 8-4.1s7.96 1.5 8 4.1C30.2 32.3 27.3 34 24 34z" fill="var(--sage)" />
        </svg>
      </div>
      <h1 style={styles.welcomeTitle}>Welcome to Transformation Companion</h1>
      <p style={styles.welcomeSubtitle}>
        Let's get your workspace set up so the app works the way you do.
      </p>
      <button style={styles.primaryBtnFull} onClick={onNext}>Get started</button>
      <p style={styles.stepCount}>Takes about a minute</p>
    </div>
  );
}

function StepAbout({ form, setForm, onBack, onNext, canAdvance }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>About you</h2>
      <p style={styles.stepSubtitle}>This helps personalize your workspace.</p>
      <div style={styles.field}>
        <label style={styles.label}>Your name *</label>
        <input
          style={styles.input}
          placeholder="e.g. Sarah Mitchell"
          value={form.fullName}
          onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
          autoFocus
        />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Business name <span style={styles.optional}>(optional)</span></label>
        <input
          style={styles.input}
          placeholder="e.g. Elevate Coaching Co."
          value={form.practiceName}
          onChange={e => setForm(f => ({ ...f, practiceName: e.target.value }))}
        />
      </div>
      <div style={styles.navRow}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <button style={{ ...styles.primaryBtn, opacity: canAdvance ? 1 : 0.4 }}
          onClick={onNext} disabled={!canAdvance}>Continue</button>
      </div>
    </div>
  );
}

function StepModalities({ form, setForm, modalityGroups, onBack, onNext }) {
  const [openGroups, setOpenGroups] = useState({});
  const [openGroupsInitialized, setOpenGroupsInitialized] = useState(false);

  useEffect(() => {
    if (openGroupsInitialized || modalityGroups.length === 0) return;
    setOpenGroups(computeInitialOpenGroups(modalityGroups, form.modalities));
    setOpenGroupsInitialized(true);
    // form.modalities intentionally excluded — this seeds which accordion
    // groups start open once, it shouldn't re-run as the coach keeps toggling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalityGroups, openGroupsInitialized]);

  const toggleModality = (name) => {
    setForm(f => ({
      ...f,
      modalities: f.modalities.includes(name) ? f.modalities.filter(x => x !== name) : [...f.modalities, name],
    }));
  };

  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>What do you practice?</h2>
      <p style={styles.stepSubtitle}>Select all that apply — this is your menu, not something applied automatically. You can change it any time in Settings.</p>
      <div style={{ width: '100%', textAlign: 'left' }}>
        <ModalitySelector
          modalityGroups={modalityGroups}
          selected={form.modalities}
          onToggle={toggleModality}
          openGroups={openGroups}
          onToggleGroup={g => setOpenGroups(o => ({ ...o, [g]: !o[g] }))}
        />
      </div>
      <div style={styles.navRow}>
        <button style={styles.ghostBtn} onClick={onBack}>Back</button>
        <button style={styles.primaryBtn} onClick={onNext}>Continue</button>
      </div>
    </div>
  );
}

function StepOfferings({ offerings, setOfferings, modalityGroups, defaultModalities, onBack, onNext }) {
  const [showModal, setShowModal] = useState(false);
  const [editingOffering, setEditingOffering] = useState(null);

  const handleSaved = (saved) => {
    setOfferings(prev => {
      const exists = prev.some(o => o.id === saved.id);
      return exists ? prev.map(o => o.id === saved.id ? saved : o) : [...prev, saved];
    });
    setShowModal(false);
    setEditingOffering(null);
  };

  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>List your offerings</h2>
      <p style={styles.stepSubtitle}>Everything you run. Add as many as you like — you can edit titles or add more anytime.</p>
      <div style={{ width: '100%' }}>
        {offerings.map(o => (
          <div key={o.id} style={styles.offeringRow}>
            <div style={{ flex: 1 }}>
              <div style={styles.offeringRowTitle}>
                {o.name}
                {o.format && (
                  <span style={{ ...styles.offeringFormatBadge, ...(o.format === 'group' ? styles.offeringFormatBadgeGroup : {}) }}>
                    {o.format === 'group' ? 'Group' : '1:1'}
                  </span>
                )}
              </div>
              {(o.modalities || []).length > 0 && (
                <div style={styles.groupChipRow}>
                  {o.modalities.map(m => <span key={m} style={styles.groupChip}>{m}</span>)}
                </div>
              )}
            </div>
            <button style={styles.offeringEditBtn} onClick={() => { setEditingOffering(o); setShowModal(true); }}>✎</button>
          </div>
        ))}
        <button style={styles.addOfferingBtn} onClick={() => { setEditingOffering(null); setShowModal(true); }}>
          + Add offering type
        </button>
      </div>
      <div style={styles.navRow}>
        <button style={styles.ghostBtn} onClick={onBack}>Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={styles.stepCount}>{offerings.length} added</span>
          <button style={styles.primaryBtn} onClick={onNext}>Continue</button>
        </div>
      </div>

      <OfferingFormModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingOffering(null); }}
        onSaved={handleSaved}
        existingOffering={editingOffering}
        modalityGroups={modalityGroups}
        defaultModalities={defaultModalities}
      />
    </div>
  );
}

function StepDone({ form, saving, onTour, onSkip }) {
  const name = form.fullName.split(' ')[0] || 'there';
  return (
    <div style={styles.stepContent}>
      <div style={styles.doneIcon}>🎉</div>
      <h2 style={styles.stepTitle}>You're all set, {name}!</h2>
      <p style={styles.stepSubtitle}>
        Your workspace is ready to go — notifications and anything else are in Settings whenever you need them. Want a quick walkthrough of the app first?
      </p>
      <button style={{ ...styles.primaryBtnFull, marginBottom: '12px' }} onClick={onTour} disabled={saving}>
        {saving ? 'Saving…' : 'Take a quick tour'}
      </button>
      <button style={styles.ghostBtn} onClick={onSkip} disabled={saving}>
        Skip — go to Settings
      </button>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'var(--cream)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '520px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '3px',
    background: 'var(--border-light)',
  },
  progressFill: {
    height: '100%',
    background: 'var(--sage)',
    transition: 'width 0.3s ease',
  },
  stepContent: {
    padding: '48px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  logo: { marginBottom: '20px' },
  welcomeTitle: {
    fontFamily: "'Lora', serif",
    fontSize: '26px',
    fontWeight: '600',
    color: 'var(--text-dark)',
    margin: '0 0 12px',
    lineHeight: 1.3,
  },
  welcomeSubtitle: {
    fontSize: '15px',
    color: 'var(--text-mid)',
    margin: '0 0 32px',
    lineHeight: 1.6,
    maxWidth: '380px',
  },
  stepTitle: {
    fontFamily: "'Lora', serif",
    fontSize: '22px',
    fontWeight: '600',
    color: 'var(--text-dark)',
    margin: '0 0 8px',
    alignSelf: 'flex-start',
  },
  stepSubtitle: {
    fontSize: '14px',
    color: 'var(--text-mid)',
    margin: '0 0 24px',
    lineHeight: 1.6,
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  field: { width: '100%', marginBottom: '16px', textAlign: 'left' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '6px' },
  optional: { fontWeight: '400', color: 'var(--text-soft)' },
  input: {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border)',
    fontSize: '14px', color: 'var(--text-dark)',
    fontFamily: "'DM Sans', sans-serif",
    background: '#fff', outline: 'none',
  },
  groupChipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  groupChip: {
    padding: '4px 9px', borderRadius: '20px',
    border: '1px solid var(--sage)', background: 'var(--mint)',
    color: 'var(--sage)', fontSize: '11.5px', fontWeight: '500',
  },
  offeringRow: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '14px 16px', borderRadius: '10px',
    border: '1px solid var(--border)', background: '#fff',
    marginBottom: '10px', textAlign: 'left',
  },
  offeringRowTitle: {
    fontSize: '14.5px', fontWeight: '600', color: 'var(--text-dark)',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  offeringFormatBadge: {
    fontSize: '11px', padding: '2px 8px', borderRadius: '20px',
    background: 'var(--gold-pale)', color: 'var(--gold)', fontWeight: '500',
  },
  offeringFormatBadgeGroup: { background: 'var(--border-light)', color: 'var(--text-mid)' },
  offeringEditBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '15px', color: 'var(--text-faint)', padding: '2px',
  },
  addOfferingBtn: {
    display: 'block', width: '100%', textAlign: 'center',
    padding: '14px', borderRadius: '10px',
    border: '1px dashed var(--border)', background: 'none',
    color: 'var(--text-mid)', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500',
  },
  navRow: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' },
  primaryBtn: {
    padding: '11px 28px', borderRadius: '8px',
    background: 'var(--sage)', color: '#fff',
    border: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
    fontFamily: "'DM Sans', sans-serif",
  },
  primaryBtnFull: {
    padding: '13px 28px', borderRadius: '8px',
    background: 'var(--sage)', color: '#fff',
    border: 'none', cursor: 'pointer',
    fontSize: '15px', fontWeight: '600',
    fontFamily: "'DM Sans', sans-serif",
    width: '100%',
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: 'var(--text-soft)',
    fontFamily: "'DM Sans', sans-serif",
    padding: '8px 4px',
    display: 'flex', alignItems: 'center', gap: '4px',
  },
  ghostBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: 'var(--text-soft)',
    fontFamily: "'DM Sans', sans-serif", padding: '8px 0',
  },
  stepCount: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '12px' },
  doneIcon: { fontSize: '48px', marginBottom: '16px' },
};
